import { z } from 'zod';
import { activeTotalsSchema } from '@/api/listTotals';
import { paginationMetaSchema } from '@/api/pagination';
import {
  monthStartDateSchema,
  optionalPositiveIdSchema,
  todayDateSchema,
} from '@/utils/searchParams';

export const promoDistributionRowSchema = z.object({
  promoItemId: z.coerce.number(),
  itemNo: z.string(),
  itemCode: z.string().nullable(),
  name: z.string(),
  typeName: z.string().nullable(),
  distributedQty: z.number(),
});

export const promoDistributionResponseSchema = z.object({
  data: z.array(promoDistributionRowSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({ distributedQty: z.number() }),
  }),
});

export const promoMemberSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  userMobile: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  distributedQty: z.number(),
});

export const promoMemberListSchema = z.array(promoMemberSchema);

export const promoInstituteSchema = z.object({
  recordId: z.coerce.number(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteDivision: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  instituteThana: z.string().nullable(),
  workTypeCode: z.string().nullable(),
  teacherMobiles: z.string().nullable(),
  givenDate: z.string(),
  distributedQty: z.number(),
});

export const promoInstituteListSchema = z.array(promoInstituteSchema);

export const promoItemTypeSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  slNum: z.coerce.number().nullable(),
  isActive: z.boolean(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  itemCount: z.number(),
});

export const promoItemTypeResponseSchema = z.object({
  data: z.array(promoItemTypeSchema),
  meta: paginationMetaSchema.extend({
    totals: activeTotalsSchema.extend({ items: z.number() }),
  }),
});

export const promoItemSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  itemTypeId: z.coerce.number().nullable(),
  typeName: z.string().nullable(),
  bookEditionNo: z.coerce.number().nullable(),
  slNum: z.coerce.number().nullable(),
  isActive: z.boolean(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  isView: z.boolean(),
  isTabView: z.boolean(),
});

export const promoItemResponseSchema = z.object({
  data: z.array(promoItemSchema),
  meta: paginationMetaSchema.extend({
    totals: activeTotalsSchema.extend({ itemTypes: z.number() }),
  }),
});

export const userPromoSchema = z.object({
  id: z.coerce.number(),
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  assignDate: z.string(),
  targetDateFrom: z.string().nullable(),
  targetDateTo: z.string().nullable(),
  isActive: z.boolean(),
  itemCount: z.number(),
  totalQty: z.number(),
});

export const userPromoResponseSchema = z.object({
  data: z.array(userPromoSchema),
  meta: paginationMetaSchema.extend({
    totals: activeTotalsSchema.extend({ users: z.number(), totalQty: z.number() }),
  }),
});

export type PromoDistributionRow = z.infer<typeof promoDistributionRowSchema>;
export type PromoDistributionResponse = z.infer<typeof promoDistributionResponseSchema>;
export type PromoMember = z.infer<typeof promoMemberSchema>;
export type PromoInstitute = z.infer<typeof promoInstituteSchema>;
export type PromoItemType = z.infer<typeof promoItemTypeSchema>;
export type PromoItemTypeResponse = z.infer<typeof promoItemTypeResponseSchema>;
export type PromoItem = z.infer<typeof promoItemSchema>;
export type PromoItemResponse = z.infer<typeof promoItemResponseSchema>;
export type UserPromo = z.infer<typeof userPromoSchema>;
export type UserPromoResponse = z.infer<typeof userPromoResponseSchema>;

export const promoAdjustmentSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  promoItemId: z.coerce.number().optional(),
  itemTypeId: z.coerce.number().optional(),
  itemCode: z.string().optional(),
  search: z.string().optional(),
});

export const catalogueStatusSchema = z.enum(['active', 'inactive', 'all']).catch('all');

export const promoCatalogueSearchSchema = z.object({
  search: z.string().optional(),
  activeState: catalogueStatusSchema.default('all'),
});

export const promoItemSearchSchema = promoCatalogueSearchSchema.extend({
  itemTypeId: z.coerce.number().optional(),
  itemCode: z.string().optional(),
});

export type PromoAdjustmentSearch = z.infer<typeof promoAdjustmentSearchSchema>;
export type CatalogueStatus = z.infer<typeof catalogueStatusSchema>;
export type PromoCatalogueSearch = z.infer<typeof promoCatalogueSearchSchema>;
export type PromoItemSearch = z.infer<typeof promoItemSearchSchema>;

export interface PromoCatalogueParams {
  search?: string | undefined;
  isActive?: boolean | undefined;
}

export interface PromoItemParams extends PromoCatalogueParams {
  itemTypeId?: number | undefined;
  itemCode?: string | undefined;
}

export const promoZmSearchSchema = z.object({
  dateFrom: monthStartDateSchema,
  dateTo: todayDateSchema,
  divisionId: optionalPositiveIdSchema,
  districtId: optionalPositiveIdSchema,
  thanaId: optionalPositiveIdSchema,
});

export type PromoDrillParams = z.infer<typeof promoZmSearchSchema>;

export type PromoAdjustmentUnit = 'promotion' | 'marketPromotion';

export const promoItemTypeFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(200, 'Name must be 200 characters or fewer'),
  code: z
    .string()
    .trim()
    .min(1, 'Code is required')
    .max(100, 'Code must be 100 characters or fewer'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer'),
  slNum: z.string().trim(),
  isActive: z.boolean(),
  activeFrom: z.string().trim(),
  activeTo: z.string().trim(),
});

export const promoItemFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(500, 'Name must be 500 characters or fewer'),
  code: z
    .string()
    .trim()
    .min(1, 'Code is required')
    .max(100, 'Code must be 100 characters or fewer'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer'),
  itemTypeId: z.number().optional(),
  bookEditionNo: z.number().optional(),
  slNum: z.string().trim(),
  isActive: z.boolean(),
  activeFrom: z.string().trim(),
  activeTo: z.string().trim(),
  isView: z.boolean(),
  isTabView: z.boolean(),
});

export type PromoItemTypeFormValues = z.infer<typeof promoItemTypeFormSchema>;
export type PromoItemFormValues = z.infer<typeof promoItemFormSchema>;

export interface PromoItemTypePayload {
  name: string;
  code: string;
  description?: string;
  slNum?: number;
  isActive: boolean;
  activeFrom?: string;
  activeTo?: string;
}

export interface PromoItemPayload {
  name: string;
  code: string;
  description?: string;
  itemTypeId?: number;
  bookEditionNo?: number;
  slNum?: number;
  isActive: boolean;
  activeFrom?: string;
  activeTo?: string;
  isView: boolean;
  isTabView: boolean;
}
